import React, { useCallback, useEffect, useState } from "react";
import API from "../services/api";
import { Button, Card, Input, Modal, Space, Switch, Table, Tag, Typography } from "antd";
import RowActions from "../components/RowActions";
import {
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import CustomForm from "../components/CustomForm";
import { useSnackbar } from "notistack";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

const UserManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearchTerm,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
      };
      const response = await API.get("/users", { params });
      setUsers(response.data.data || []);
      setTotalCount(
        response.data.meta && typeof response.data.meta.total === "number"
          ? response.data.meta.total
          : Array.isArray(response.data.data)
            ? response.data.data.length
            : 0
      );
    } catch (error) {
      enqueueSnackbar("Error fetching users", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchTerm, sortConfig.key, sortConfig.direction, enqueueSnackbar]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, sortConfig.key, sortConfig.direction]);

  const userFields = [
    { name: "name", label: "Full Name", required: true, width: 24 },
    { name: "email", label: "Email Address", type: "email", required: true, width: 24 },
    {
      name: "role",
      label: "User Role",
      type: "select",
      required: true,
      width: 12,
      options: [
        { value: "master_admin", label: "Master Admin" },
        { value: "plan_admin", label: "Plan Admin" },
      ],
    },
    ...(editingUser
      ? []
      : [
        {
          name: "password",
          label: "Password",
          type: "password",
          required: true,
          width: 12,
          inputProps: { autoComplete: "new-password" },
        },
      ]),
  ];

  const getRoleColor = (role) => {
    switch (role) {
      case "master_admin":
        return "red";
      case "plan_admin":
        return "gold";
      default:
        return "default";
    }
  };

  const handleFormSubmit = async (formData) => {
    setLoading(true);
    try {
      const { id, password_hash, ...payload } = formData;
      if (editingUser) {
        await API.put(`/users/${editingUser.id}`, payload);
        enqueueSnackbar("User updated successfully", { variant: "success" });
      } else {
        await API.post("/users", payload);
        enqueueSnackbar("User created successfully", { variant: "success" });
      }
      fetchUsers();
      setOpen(false);
      setEditingUser(null);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || "Error saving user", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (user) => {
    const updatedStatus = !user.is_active;
    try {
      await API.put(`/users/${user.id}`, { is_active: updatedStatus });
      enqueueSnackbar(`User ${updatedStatus ? "activated" : "deactivated"} successfully`, {
        variant: "success",
      });
      fetchUsers();
    } catch (error) {
      enqueueSnackbar("Error updating user status", { variant: "error" });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await API.delete(`/users/${userToDelete.id}`);
      enqueueSnackbar("User deleted successfully", { variant: "success" });
      fetchUsers();
    } catch (error) {
      enqueueSnackbar("Error deleting user", { variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: sortConfig.key === "name" ? `${sortConfig.direction}end` : null,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      sortOrder: sortConfig.key === "email" ? `${sortConfig.direction}end` : null,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      sorter: true,
      sortOrder: sortConfig.key === "role" ? `${sortConfig.direction}end` : null,
      render: (value) => <Tag color={getRoleColor(value)}>{value}</Tag>,
    },
    {
      title: "Status",
      key: "status",
      render: (_, user) => (
        <Space>
          <Switch checked={user.is_active} onChange={() => handleStatusToggle(user)} disabled={loading} />
          <Tag color={user.is_active ? "green" : "default"}>{user.is_active ? "Active" : "Inactive"}</Tag>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, user) => (
        <RowActions
          disabled={loading}
          items={[
            {
              key: "edit",
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => {
                setEditingUser(user);
                setOpen(true);
              },
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => {
                setUserToDelete(user);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const emptyState = (
    <div style={{ padding: 48, textAlign: "center" }}>
      <UserOutlined style={{ fontSize: 56, color: "rgba(100, 116, 139, 0.45)" }} />
      <Typography.Title level={4} style={{ marginTop: 16 }}>
        No users found
      </Typography.Title>
      <Typography.Text type="secondary">
        {searchTerm ? "Clear the search to view all users." : "Create the first user to get started."}
      </Typography.Text>
      <div style={{ marginTop: 20 }}>
        {searchTerm ? (
          <Button icon={<ClearOutlined />} onClick={() => setSearchTerm("")}>
            Clear search
          </Button>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Add user
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card className="app-page-card" variant="borderless" styles={{ body: { padding: 24 } }}>
      <div className="page-toolbar">
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            User Management
          </Typography.Title>
        </div>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchUsers} loading={loading} />
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setOpen(true)}
            disabled={loading}
          >
            Add User
          </Button>
        </Space>
      </div>

      <Space style={{ width: "100%", marginBottom: 20 }} wrap>
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search users..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          style={{ width: 300 }}
          disabled={loading}
        />
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={users}
        loading={loading}
        locale={{ emptyText: emptyState }}
        pagination={{
          current: page + 1,
          pageSize: rowsPerPage,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 25],
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage - 1);
            setRowsPerPage(nextPageSize);
          },
        }}
        onChange={(_, __, sorter) => {
          if (sorter?.field) {
            setSortConfig({
              key: sorter.field,
              direction: sorter.order === "descend" ? "desc" : "asc",
            });
          }
        }}
      />

      <Modal
        open={open}
        destroyOnHidden
        title={
          <div
            style={{
              textAlign: "center",
              width: "100%",
              paddingRight: 24,
            }}
          >
            {editingUser ? "Edit User" : "Add New User"}
          </div>
        }
        onCancel={() => {
          if (!loading) {
            setOpen(false);
            setEditingUser(null);
          }
        }}
        footer={null}
        width={680}
      >
        <CustomForm
          fields={userFields}
          initialValues={editingUser || {}}
          formResetKey={editingUser?.id || "create"}
          onSubmit={handleFormSubmit}
          loading={loading}
          buttonText={editingUser ? "Update User" : "Create User"}
          onCancel={() => {
            setOpen(false);
            setEditingUser(null);
          }}
        />
      </Modal>

      <Modal
        open={deleteDialogOpen}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        onOk={handleDeleteConfirm}
        okText="Delete"
        okButtonProps={{ danger: true, loading }}
        title="Confirm delete"
      >
        <Space orientation="vertical" size={6}>
          <Typography.Text>Are you sure you want to delete this user?</Typography.Text>
          {userToDelete && (
            <>
              <Typography.Text>Name: {userToDelete.name}</Typography.Text>
              <Typography.Text>Email: {userToDelete.email}</Typography.Text>
              <Typography.Text>
                Role: <Tag color={getRoleColor(userToDelete.role)}>{userToDelete.role}</Tag>
              </Typography.Text>
            </>
          )}
        </Space>
      </Modal>
    </Card>
  );
};

export default UserManagement;
